import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Panel } from 'primereact/panel';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import './App.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3006/api';

function TeacherAttendanceReport() {
    const { teacherId } = useParams();
    const [searchParams] = useSearchParams();
    const teacherName = searchParams.get('name') || 'Teacher';
    const month = searchParams.get('month') || new Date().toISOString().slice(0, 7);
    
    const [attendanceData, setAttendanceData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState({ present: 0, absent: 0, leave: 0, total: 0 });

    const monthDisplay = new Date(month + '-01').toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long' 
    });

    const fetchAttendanceData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/attendance/teacher/${teacherId}?month=${month}`);
            const data = response.data.data || [];
            setAttendanceData(data);
            
            let present = 0, absent = 0, leave = 0;
            data.forEach(record => {
                if (record.status === 'P' || record.in_time) present++;
                else if (record.status === 'A') absent++;
                else if (record.status === 'L') leave++;
            });
            setSummary({ present, absent, leave, total: data.length });
        } catch (error) {
            console.error('Error fetching teacher attendance:', error);
            setAttendanceData([]);
        } finally {
            setLoading(false);
        }
    }, [teacherId, month]);

    useEffect(() => {
        fetchAttendanceData();
    }, [fetchAttendanceData]);

    const statusTemplate = (rowData) => {
        const status = rowData.status || (rowData.in_time ? 'P' : 'A');
        const isUnverified = rowData.is_manual_entry === 1 && rowData.is_verified === 0;
        const colors = {
            'P': { bg: '#d4edda', color: '#155724' },
            'A': { bg: '#f8d7da', color: '#721c24' },
            'L': { bg: '#fff3cd', color: '#856404' }
        };
        const style = colors[status] || { bg: '#e9ecef', color: '#495057' };
        const labels = { 'P': 'Present', 'A': 'Absent', 'L': 'Leave' };
        
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{
                    backgroundColor: style.bg,
                    color: style.color,
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500'
                }}>
                    {labels[status] || status || '-'}
                </span>
                {isUnverified && (
                    <span style={{
                        backgroundColor: '#fef3c7',
                        color: '#92400e',
                        padding: '2px 8px',
                        borderRadius: '8px',
                        fontSize: '10px',
                        fontWeight: '600'
                    }}>
                        To be Approved
                    </span>
                )}
            </div>
        );
    };

    const dateTemplate = (rowData) => {
        if (!rowData.date) return '-';
        const date = new Date(rowData.date);
        return date.toLocaleDateString('en-US', { 
            weekday: 'short', 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
        });
    };

    const timeTemplate = (rowData, field) => {
        const time = rowData[field];
        return time ? time.substring(0, 5) : '-';
    };

    const formatDateForExport = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
    };

    const handleDownloadExcel = () => {
        const exportData = attendanceData.map(record => ({
            'Date': formatDateForExport(record.date),
            'Status': record.status === 'P' ? 'Present' : record.status === 'A' ? 'Absent' : record.status === 'L' ? 'Leave' : record.in_time ? 'Present' : 'Absent',
            'In Time': record.in_time ? record.in_time.substring(0, 5) : '-',
            'Out Time': record.out_time ? record.out_time.substring(0, 5) : '-'
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Attendance');
        XLSX.writeFile(wb, `${teacherName.replace(/\s+/g, '_')}_${month}.xlsx`);
    };

    const handleDownloadPDF = () => {
        const doc = new jsPDF();
        
        // Header
        doc.setFontSize(16);
        doc.setTextColor(30, 58, 95);
        doc.text(teacherName.toUpperCase(), 105, 20, { align: 'center' });
        
        doc.setFontSize(12);
        doc.setTextColor(100);
        doc.text(`Monthly Attendance Report - ${monthDisplay}`, 105, 28, { align: 'center' });
        
        // Summary
        doc.setFontSize(10);
        doc.setTextColor(0);
        doc.text(`Present: ${summary.present} | Absent: ${summary.absent} | Leave: ${summary.leave} | Total: ${summary.total}`, 105, 38, { align: 'center' });
        
        // Table
        const tableData = attendanceData.map(record => [
            formatDateForExport(record.date),
            record.status === 'P' ? 'Present' : record.status === 'A' ? 'Absent' : record.status === 'L' ? 'Leave' : record.in_time ? 'Present' : 'Absent',
            record.in_time ? record.in_time.substring(0, 5) : '-',
            record.out_time ? record.out_time.substring(0, 5) : '-'
        ]);

        autoTable(doc, {
            head: [['Date', 'Status', 'In Time', 'Out Time']],
            body: tableData,
            startY: 45,
            styles: { fontSize: 9 },
            headStyles: { fillColor: [30, 58, 95] },
            alternateRowStyles: { fillColor: [245, 245, 245] }
        });
        
        // Footer
        doc.setFontSize(8);
        doc.setTextColor(128);
        doc.text(`Generated on ${new Date().toLocaleString()}`, 14, doc.internal.pageSize.height - 10);
        
        const pdfUrl = doc.output('bloburl');
        window.open(pdfUrl, '_blank');
    };

    return (
        <div className="card p-fluid">
            {loading && (
                <div className="loading-overlay">
                    <ProgressSpinner />
                </div>
            )}

            <div className="p-grid p-justify-center" style={{ marginTop: '0', textAlign: 'center' }}>
                <div style={{ width: '100%', padding: '15px 20px', backgroundColor: '#f8f9fa', textAlign: 'center' }}>
                    <div style={{ fontSize: '1rem', color: '#6c757d', marginBottom: '5px' }}>{monthDisplay}</div>
                    <div style={{ fontSize: '1.2rem', color: '#495057', marginBottom: '5px' }}>Monthly Attendance Detail</div>
                    <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#1e3a5f' }}>{teacherName.toUpperCase()}</div>
                </div>

                <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', padding: '20px', flexWrap: 'wrap' }}>
                    <div style={{ backgroundColor: '#d4edda', padding: '20px 40px', borderRadius: '10px', textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#155724' }}>{summary.present}</div>
                        <div style={{ color: '#155724' }}>Present Days</div>
                    </div>
                    <div style={{ backgroundColor: '#f8d7da', padding: '20px 40px', borderRadius: '10px', textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#721c24' }}>{summary.absent}</div>
                        <div style={{ color: '#721c24' }}>Absent Days</div>
                    </div>
                    <div style={{ backgroundColor: '#fff3cd', padding: '20px 40px', borderRadius: '10px', textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#856404' }}>{summary.leave}</div>
                        <div style={{ color: '#856404' }}>Leave Days</div>
                    </div>
                    <div style={{ backgroundColor: '#e2e3e5', padding: '20px 40px', borderRadius: '10px', textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#383d41' }}>{summary.total}</div>
                        <div style={{ color: '#383d41' }}>Total Records</div>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '15px' }}>
                    <Button
                        label="Download Excel"
                        icon="pi pi-file-excel"
                        className="p-button-success"
                        onClick={handleDownloadExcel}
                        disabled={attendanceData.length === 0}
                    />
                    <Button
                        label="Download PDF"
                        icon="pi pi-file-pdf"
                        className="p-button-danger"
                        onClick={handleDownloadPDF}
                        disabled={attendanceData.length === 0}
                    />
                </div>

                <Panel className="data-panel" style={{ width: '100%', marginTop: '10px' }}>
                    <DataTable
                        value={attendanceData}
                        paginator
                        rows={25}
                        rowsPerPageOptions={[25, 50, 100]}
                        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                        loading={loading}
                        scrollable
                        scrollHeight="calc(100vh - 400px)"
                        emptyMessage="No attendance records found"
                        sortField="date"
                        sortOrder={-1}
                    >
                        <Column
                            field="date"
                            header="Date"
                            body={dateTemplate}
                            sortable
                            style={{ minWidth: '180px' }}
                        />
                        <Column
                            field="status"
                            header="Status"
                            body={statusTemplate}
                            style={{ width: '120px', textAlign: 'center' }}
                        />
                        <Column
                            field="in_time"
                            header="In Time"
                            body={(rowData) => timeTemplate(rowData, 'in_time')}
                            style={{ width: '100px', textAlign: 'center' }}
                        />
                        <Column
                            field="out_time"
                            header="Out Time"
                            body={(rowData) => timeTemplate(rowData, 'out_time')}
                            style={{ width: '100px', textAlign: 'center' }}
                        />
                    </DataTable>
                </Panel>
            </div>
        </div>
    );
}

export default TeacherAttendanceReport;
